/**
 * Banner Editor Page - Refactored with modular components
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { DndContext } from "@dnd-kit/core";
import { ArrowLeft, Save, Upload, Image, Copy, Link, ExternalLink, Lock, Plus, Trash2, Mail as MailIcon } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Import editor components
import {
  DEFAULT_BANNER_STATE,
  DraggableText,
  DraggableResultText,
  DraggableNumbers,
  ZoomControls,
  BackgroundSettings,
  ResultTextSettings,
  CustomTextSettings,
  NumberSettings,
} from "@/components/editor";

export default function BannerEditor() {
  const navigate = useNavigate();
  const { bannerId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedLotteryId = searchParams.get('lottery');
  const { canEditDestination } = useUser();
  
  const [banner, setBanner] = useState({
    ...DEFAULT_BANNER_STATE,
    lottery_id: preselectedLotteryId || "",
  });
  const [lotteries, setLotteries] = useState([]);
  const [brands, setBrands] = useState([]);
  const [lotteryResult, setLotteryResult] = useState([0, 0, 0, 0, 0, 0]);
  const [lotteryResultText, setLotteryResultText] = useState("");
  const [numberSets, setNumberSets] = useState([]);
  const [customFonts, setCustomFonts] = useState([]);
  const [defaultAssets, setDefaultAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [canvasZoom, setCanvasZoom] = useState(100);
  const fontInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Handle drag end for positioning elements
  const handleDragEnd = (event) => {
    const { active, delta } = event;
    const canvasWidth = banner.banner_width;
    const canvasHeight = banner.banner_height;
    // Account for zoom when calculating position delta
    // The drag delta is in screen pixels (scaled), so divide by zoom factor
    const zoomFactor = canvasZoom / 100;
    const adjustedDeltaX = delta.x / zoomFactor;
    const adjustedDeltaY = delta.y / zoomFactor;
    
    if (active.id === 'draggable-text') {
      const newX = banner.text_position_x + (adjustedDeltaX / canvasWidth) * 100;
      const newY = banner.text_position_y + (adjustedDeltaY / canvasHeight) * 100;
      setBanner({
        ...banner,
        text_position_x: Math.max(0, Math.min(100, newX)),
        text_position_y: Math.max(0, Math.min(100, newY)),
      });
    } else if (active.id === 'draggable-result-text') {
      const newX = banner.result_text_position_x + (adjustedDeltaX / canvasWidth) * 100;
      const newY = banner.result_text_position_y + (adjustedDeltaY / canvasHeight) * 100;
      setBanner({
        ...banner,
        result_text_position_x: Math.max(0, Math.min(100, newX)),
        result_text_position_y: Math.max(0, Math.min(100, newY)),
      });
    } else if (active.id === 'draggable-numbers') {
      const newX = banner.position_x + (adjustedDeltaX / canvasWidth) * 100;
      const newY = banner.position_y + (adjustedDeltaY / canvasHeight) * 100;
      setBanner({
        ...banner,
        position_x: Math.max(0, Math.min(100, newX)),
        position_y: Math.max(0, Math.min(100, newY)),
      });
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [lotteriesRes, assetsRes, setsRes, fontsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/lotteries`),
        axios.get(`${API}/default-assets`),
        axios.get(`${API}/number-sets`),
        axios.get(`${API}/custom-fonts`).catch(() => ({ data: [] })),
        axios.get(`${API}/brands`).catch(() => ({ data: [] })),
      ]);

      setLotteries(lotteriesRes.data);
      setBrands(brandsRes.data);
      setCustomFonts(fontsRes.data);
      
      const assetsMap = {};
      assetsRes.data.forEach((asset) => {
        assetsMap[asset.number] = asset.image_data;
      });
      setDefaultAssets(assetsMap);
      setNumberSets(setsRes.data);

      if (bannerId) {
        const bannerRes = await axios.get(`${API}/banners/${bannerId}`);
        const bannerData = bannerRes.data;
        // Migrate legacy single background to array if needed
        if ((!bannerData.background_images || bannerData.background_images.length === 0) && bannerData.background_image) {
          bannerData.background_images = [bannerData.background_image];
          bannerData.background_index = 0;
        }
        setBanner(bannerData);
        
        if (bannerRes.data.lottery_id) {
          const lottery = lotteriesRes.data.find(l => l.id === bannerRes.data.lottery_id);
          if (lottery) {
            setLotteryResult(lottery.current_result || [0, 0, 0, 0, 0, 0]);
            setLotteryResultText(lottery.result_text || "");
          }
        }
      } else if (preselectedLotteryId) {
        const lottery = lotteriesRes.data.find(l => l.id === preselectedLotteryId);
        if (lottery) {
          setLotteryResult(lottery.current_result || [0, 0, 0, 0, 0, 0]);
          setLotteryResultText(lottery.result_text || "");
          setBanner(prev => ({ ...prev, lottery_id: preselectedLotteryId }));
        }
      } else if (lotteriesRes.data.length > 0) {
        const firstLottery = lotteriesRes.data[0];
        setLotteryResult(firstLottery.current_result || [0, 0, 0, 0, 0, 0]);
        setLotteryResultText(firstLottery.result_text || "");
        setBanner(prev => ({ ...prev, lottery_id: firstLottery.id }));
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [bannerId, preselectedLotteryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleLotteryChange = (lotteryId) => {
    setBanner({ ...banner, lottery_id: lotteryId });
    const lottery = lotteries.find(l => l.id === lotteryId);
    if (lottery) {
      setLotteryResult(lottery.current_result || [0, 0, 0, 0, 0, 0]);
      setLotteryResultText(lottery.result_text || "");
    }
  };

  const handleFontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(ttf|otf|woff|woff2)$/i)) {
      toast.error("Please upload a .ttf, .otf, .woff, or .woff2 font file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Font file too large. Please use a file under 5MB");
      return;
    }

    setUploadingFont(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name.replace(/\.(ttf|otf|woff|woff2)$/i, ''));
    formData.append("font_family", file.name.replace(/\.(ttf|otf|woff|woff2)$/i, ''));

    try {
      const response = await axios.post(`${API}/custom-fonts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCustomFonts([...customFonts, response.data]);
      toast.success("Font uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload font");
    } finally {
      setUploadingFont(false);
    }
  };

  const handleBackgroundUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Please use an image under 5MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data && response.data.image_data) {
        const imageData = response.data.image_data;
        const currentBgs = banner.background_images || [];
        // If this is the first upload and there's a legacy background_image, include it
        const newBgs = currentBgs.length === 0 && banner.background_image
          ? [banner.background_image, imageData]
          : [...currentBgs, imageData];
        const newIndex = newBgs.length - 1;
        setBanner({
          ...banner,
          background_images: newBgs,
          background_index: newIndex,
          background_image: imageData,
        });
        toast.success("Background added");
      }
    } catch (error) {
      toast.error("Failed to upload background. Please try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!banner.name) {
      toast.error("Please provide a banner name");
      return;
    }
    
    const hasBackground = banner.background_image || (banner.background_images && banner.background_images.length > 0);
    if (!hasBackground && !banner.use_transparent_background) {
      toast.error("Please upload a background image or enable transparent background");
      return;
    }
    
    if (!banner.lottery_id) {
      toast.error("Please select a lottery for this banner");
      return;
    }

    setSaving(true);
    try {
      // Round all numeric values to integers
      const bannerData = {
        ...banner,
        banner_width: Math.round(banner.banner_width),
        banner_height: Math.round(banner.banner_height),
        position_x: Math.round(banner.position_x),
        position_y: Math.round(banner.position_y),
        number_gap: Math.round(banner.number_gap || 2),
        result_text_position_x: Math.round(banner.result_text_position_x || 50),
        result_text_position_y: Math.round(banner.result_text_position_y || 10),
        result_text_size: Math.round(banner.result_text_size || 18),
        result_text_font_weight: Math.round(banner.result_text_font_weight || 700),
        result_text_shadow_x: Math.round(banner.result_text_shadow_x || 2),
        result_text_shadow_y: Math.round(banner.result_text_shadow_y || 2),
        result_text_shadow_blur: Math.round(banner.result_text_shadow_blur || 4),
        text_position_x: Math.round(banner.text_position_x),
        text_position_y: Math.round(banner.text_position_y),
        text_size: Math.round(banner.text_size),
        text_font_weight: Math.round(banner.text_font_weight || 700),
        text_shadow_x: Math.round(banner.text_shadow_x || 2),
        text_shadow_y: Math.round(banner.text_shadow_y || 2),
        text_shadow_blur: Math.round(banner.text_shadow_blur || 4),
        number_text_size: Math.round(banner.number_text_size),
      };
      
      if (bannerId) {
        await axios.put(`${API}/banners/${bannerId}`, bannerData);
        toast.success("Banner updated successfully");
      } else {
        const response = await axios.post(`${API}/banners`, bannerData);
        toast.success("Banner created successfully");
        navigate(`/banner-editor/${response.data.id}`);
      }
    } catch (error) {
      let errorMessage = "Failed to save banner";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = Array.isArray(detail) 
          ? detail.map(err => err.msg || err.message).join(", ")
          : detail;
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const renderNumbers = () => {
    if (!banner.use_number_images) {
      const textStyle = {
        fontFamily: banner.number_text_font,
        color: banner.number_text_color,
        fontSize: `${banner.number_text_size}px`,
        textShadow: banner.number_text_shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
      };
      
      return lotteryResult.map((num, index) => (
        <span key={`num-${index}-${num}`} style={textStyle} className="font-bold px-2">
          {num}
        </span>
      ));
    }
    
    let assets = defaultAssets;
    if (banner.number_set_id) {
      const selectedSet = numberSets.find(s => s.id === banner.number_set_id);
      if (selectedSet && selectedSet.numbers) {
        assets = selectedSet.numbers;
      }
    }
    
    return lotteryResult.map((num, index) => {
      const imageData = assets[num.toString()] || assets[num];
      return (
        <img
          key={`img-${index}-${num}`}
          src={imageData || `https://via.placeholder.com/60x60/10B981/020617?text=${num}`}
          alt={`Number ${num}`}
          className="w-12 h-12 object-contain"
        />
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const hasBackground = banner.background_image || (banner.background_images && banner.background_images.length > 0) || banner.use_transparent_background;
  const canSave = banner.name && hasBackground && banner.lottery_id;

  // Generate @font-face rules for custom fonts
  const customFontStyles = customFonts.map(font => `
    @font-face {
      font-family: '${font.font_family}';
      src: url('${font.font_data}');
    }
  `).join('\n');

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#0a0a0f]">
      {/* Inject custom font styles */}
      {customFonts.length > 0 && <style>{customFontStyles}</style>}
      
      {/* Header */}
      <nav className="bg-[#12121a] border-b border-white/5 z-50">
        <div className="px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/")}
              data-testid="back-button"
              variant="ghost"
              size="icon"
              className="hover:bg-white/5 text-gray-400"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">
                {bannerId ? "Edit Banner" : "Create Banner"}
              </h1>
              {banner.name && <p className="text-xs text-gray-500">{banner.name}</p>}
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !canSave}
            data-testid="save-banner-button"
            className="bg-[#10B981] hover:bg-[#059669] text-white h-9 px-4 font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Banner"}
          </Button>
        </div>
      </nav>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-72 bg-[#12121a] border-r border-white/5 flex flex-col">
          {/* Validation Alert */}
          {!canSave && (
            <div className="p-3 bg-amber-500/10 border-b border-amber-500/20">
              <p className="text-amber-500 text-xs font-medium mb-1">Required to save:</p>
              <ul className="text-gray-400 text-xs space-y-0.5">
                {!banner.lottery_id && <li>• Select a lottery</li>}
                {!banner.name && <li>• Banner name</li>}
                {!hasBackground && <li>• Background image or transparent</li>}
              </ul>
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Banner Name */}
            <div>
              <Label className="text-xs text-gray-400 uppercase tracking-wider">Banner Name</Label>
              <Input
                value={banner.name}
                onChange={(e) => setBanner({ ...banner, name: e.target.value })}
                placeholder="My Lottery Banner"
                data-testid="banner-name-input"
                className="mt-2 h-9 bg-[#1a1a24] border-white/10 text-white placeholder:text-gray-600"
              />
            </div>

            {/* Lottery Selection */}
            <div>
              <Label className="text-xs text-gray-400 uppercase tracking-wider">Lottery</Label>
              <select
                value={banner.lottery_id || ""}
                onChange={(e) => handleLotteryChange(e.target.value)}
                className="w-full mt-2 h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-white focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981] outline-none"
                data-testid="lottery-select"
              >
                <option value="">Select a lottery...</option>
                {lotteries.map(lottery => (
                  <option key={lottery.id} value={lottery.id}>
                    {lottery.name} ({lottery.numbers_count} numbers)
                  </option>
                ))}
              </select>
            </div>

            {/* Brand Selection */}
            <div>
              <Label className="text-xs text-gray-400 uppercase tracking-wider">Brand</Label>
              <select
                value={banner.brand_id || ""}
                onChange={(e) => setBanner({ ...banner, brand_id: e.target.value || null })}
                className="w-full mt-2 h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-white focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981] outline-none"
                data-testid="brand-select"
              >
                <option value="">No brand</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div>
              <Label className="text-xs text-gray-400 uppercase tracking-wider">Dimensions (px)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs text-gray-500">Width</Label>
                  <Input
                    type="number"
                    value={banner.banner_width}
                    onChange={(e) => setBanner({ ...banner, banner_width: parseInt(e.target.value) || 800 })}
                    min="200" max="2000"
                    className="h-8 bg-[#1a1a24] border-white/10 text-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Height</Label>
                  <Input
                    type="number"
                    value={banner.banner_height}
                    onChange={(e) => setBanner({ ...banner, banner_height: parseInt(e.target.value) || 400 })}
                    min="100" max="2000"
                    className="h-8 bg-[#1a1a24] border-white/10 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Email Delivery */}
            <div className="border-t border-white/10 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <MailIcon className="w-3 h-3" />
                  Email Delivery
                </Label>
                <Switch
                  checked={banner.email_enabled || false}
                  onCheckedChange={(checked) => setBanner({ ...banner, email_enabled: checked })}
                  data-testid="banner-email-toggle"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Recipients</Label>
                <div className="mt-1 space-y-2">
                  {(banner.recipients || []).map((r, idx) => (
                    <div key={r.email || `new-${idx}`} className="flex items-center gap-2 bg-[#1a1a24] rounded-lg p-2 border border-white/5">
                      <div className="flex-1 min-w-0 space-y-1">
                        <Input value={r.name || ""} onChange={(e) => { const u = [...(banner.recipients || [])]; u[idx] = { ...u[idx], name: e.target.value }; setBanner({ ...banner, recipients: u }); }} placeholder="Name" className="h-7 text-xs bg-transparent border-white/10 text-white" data-testid={`recipient-name-input-${idx}`} />
                        <Input value={r.email || ""} onChange={(e) => { const u = [...(banner.recipients || [])]; u[idx] = { ...u[idx], email: e.target.value }; setBanner({ ...banner, recipients: u }); }} placeholder="email@example.com" type="email" className="h-7 text-xs bg-transparent border-white/10 text-white" data-testid={`recipient-email-input-${idx}`} />
                      </div>
                      <Switch checked={r.active !== false} onCheckedChange={(checked) => { const u = [...(banner.recipients || [])]; u[idx] = { ...u[idx], active: checked }; setBanner({ ...banner, recipients: u }); }} data-testid={`recipient-active-toggle-${idx}`} />
                      <Button variant="ghost" size="sm" onClick={() => setBanner({ ...banner, recipients: (banner.recipients || []).filter((_, i) => i !== idx) })} className="text-gray-500 hover:text-red-400 h-7 w-7 p-0" data-testid={`remove-recipient-${idx}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setBanner({ ...banner, recipients: [...(banner.recipients || []), { name: "", email: "", active: true, added_at: new Date().toISOString() }] })} className="w-full h-8 text-xs border-dashed border-white/10 text-gray-400 hover:text-white hover:border-white/20" data-testid="add-recipient-btn"><Plus className="w-3 h-3 mr-1" />Add Recipient</Button>
                </div>
              </div>
            </div>

            {/* Destination URL */}
            <div className="border-t border-white/10 pt-3">
              <Label className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <ExternalLink className="w-3 h-3" />
                Destination URL
                {!canEditDestination && <Lock className="w-3 h-3 text-amber-500" />}
              </Label>
              <Input
                value={banner.destination_url || ""}
                onChange={(e) => setBanner({ ...banner, destination_url: e.target.value })}
                placeholder={canEditDestination ? "https://example.com" : "Only owners can edit"}
                disabled={!canEditDestination}
                data-testid="destination-url-input"
                className={`mt-2 h-9 bg-[#1a1a24] border-white/10 text-white placeholder:text-gray-600 ${!canEditDestination ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
            </div>

            {/* Embed Snippet */}
            {bannerId && (
              <div>
                <Label className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Link className="w-3 h-3" />
                  Embed Snippet
                </Label>
                <div className="mt-2 p-3 bg-[#1a1a24] border border-white/10 rounded-lg">
                  <code className="text-xs text-gray-300 break-all block mb-3">
                    {banner.destination_url 
                      ? `<a href="${API}/click/${bannerId}" target="_blank" rel="noopener"><img src="${API}/banner-image/${bannerId}" alt="${banner.name || 'Banner'}" /></a>`
                      : `<img src="${API}/banner-image/${bannerId}" alt="${banner.name || 'Banner'}" />`
                    }
                  </code>
                  <Button size="sm" variant="outline" className="w-full h-7 text-xs bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20" data-testid="copy-snippet-button" onClick={() => { const s = banner.destination_url ? `<a href="${API}/click/${bannerId}" target="_blank" rel="noopener"><img src="${API}/banner-image/${bannerId}" alt="${banner.name || 'Banner'}" /></a>` : `<img src="${API}/banner-image/${bannerId}" alt="${banner.name || 'Banner'}" />`; navigator.clipboard.writeText(s).then(() => toast.success("Snippet copied!")).catch(() => toast.error("Failed to copy")); }}>
                    <Copy className="w-3 h-3 mr-2" />Copy Snippet
                  </Button>
                </div>
              </div>
            )}

            {/* Design Elements - Accordions */}
            <div className="border-t border-white/10 pt-3">
              <Label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">Design</Label>
              <Accordion type="multiple" className="space-y-1">
                <AccordionItem value="background" className="border-white/10">
                  <AccordionTrigger className="text-sm text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5">Background</AccordionTrigger>
                  <AccordionContent className="px-1 pt-2">
                    <BackgroundSettings banner={banner} setBanner={setBanner} uploading={uploading} onUpload={handleBackgroundUpload} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="numbers" className="border-white/10">
                  <AccordionTrigger className="text-sm text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5">Number Scale &amp; Gap</AccordionTrigger>
                  <AccordionContent className="px-1 pt-2 space-y-4">
                    <div>
                      <Label className="text-xs text-gray-400">Scale: {banner.scale.toFixed(1)}x</Label>
                      <Slider value={[banner.scale]} onValueChange={([v]) => setBanner({ ...banner, scale: v })} min={0.5} max={5} step={0.1} data-testid="scale-slider" className="mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Gap: {banner.number_gap}px</Label>
                      <Slider value={[banner.number_gap]} onValueChange={([v]) => setBanner({ ...banner, number_gap: v })} min={0} max={20} step={1} data-testid="number-gap-slider" className="mt-2" />
                    </div>
                    <NumberSettings banner={banner} setBanner={setBanner} numberSets={numberSets} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="result-text" className="border-white/10">
                  <AccordionTrigger className="text-sm text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5">Result Text</AccordionTrigger>
                  <AccordionContent className="px-1 pt-2">
                    <ResultTextSettings banner={banner} setBanner={setBanner} resultText={lotteryResultText} customFonts={customFonts} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="custom-text" className="border-white/10">
                  <AccordionTrigger className="text-sm text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5">Custom Text</AccordionTrigger>
                  <AccordionContent className="px-1 pt-2">
                    <CustomTextSettings banner={banner} setBanner={setBanner} customFonts={customFonts} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="fonts" className="border-white/10">
                  <AccordionTrigger className="text-sm text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5">Custom Fonts</AccordionTrigger>
                  <AccordionContent className="px-1 pt-2">
                    <input ref={fontInputRef} type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleFontUpload} className="hidden" />
                    <Button onClick={() => fontInputRef.current?.click()} disabled={uploadingFont} variant="outline" className="w-full h-8 bg-[#1a1a24] border-white/10 hover:bg-white/5 text-gray-400 text-xs">
                      <Upload className="w-3 h-3 mr-2" />{uploadingFont ? "Uploading..." : "Upload Font (.ttf, .otf)"}
                    </Button>
                    {customFonts.length > 0 && <div className="mt-2 text-xs text-gray-500">{customFonts.length} custom font(s) uploaded</div>}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 bg-[#0a0a0f] flex flex-col overflow-hidden" ref={canvasRef}>
          {/* Zoom Controls */}
          <ZoomControls zoom={canvasZoom} setZoom={setCanvasZoom} />
          
          {/* Canvas Container */}
          <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
            {(banner.background_image || banner.use_transparent_background) ? (
              <DndContext onDragEnd={handleDragEnd}>
                <div
                  className="relative rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10"
                  style={{
                    width: `${banner.banner_width}px`,
                    height: `${banner.banner_height}px`,
                    transform: `scale(${canvasZoom / 100})`,
                    transformOrigin: 'center center',
                    backgroundImage: banner.use_transparent_background 
                      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                      : banner.background_image 
                        ? `url(${banner.background_image})`
                        : `linear-gradient(${banner.background_color}, ${banner.background_color})`,
                    backgroundSize: banner.use_transparent_background 
                      ? '20px 20px' 
                      : 'cover',
                    backgroundPosition: banner.use_transparent_background 
                      ? '0 0, 0 10px, 10px -10px, -10px 0px'
                      : 'center',
                    backgroundColor: banner.use_transparent_background ? '#fff' : (banner.background_color || '#fff'),
                  }}
                  data-testid="banner-canvas"
                >
                  {banner.show_result_text && lotteryResultText && (
                    <DraggableResultText banner={banner} resultText={lotteryResultText} />
                  )}
                  {banner.show_custom_text && banner.custom_text && (
                    <DraggableText banner={banner} />
                  )}
                  <DraggableNumbers banner={banner}>{renderNumbers()}</DraggableNumbers>
                </div>
              </DndContext>
            ) : (
              <div className="text-center" data-testid="no-background-message">
                <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Image className="w-10 h-10 text-gray-600" />
                </div>
                <p className="text-gray-500 text-lg mb-2">No background image</p>
                <p className="text-gray-600 text-sm">Upload an image or enable transparent background</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
